import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft,
  Clock,
  CheckCircle2,
  MessageSquare,
  Users,
  Send,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ToolboxTalkView = () => {
  const { talkId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [talk, setTalk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [assignmentType, setAssignmentType] = useState('individual');
  const [assigning, setAssigning] = useState(false);
  
  // For completing a talk
  const [myAssignment, setMyAssignment] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    fetchData();
  }, [talkId]);

  const fetchData = async () => {
    try {
      const talkRes = await axios.get(`${API}/toolbox-talks/${talkId}`, { withCredentials: true });
      setTalk(talkRes.data);
      
      // Fetch employees and groups for assignment
      if (user?.role === 'super_admin' || user?.role === 'company_admin') {
        const [empRes, grpRes] = await Promise.all([
          axios.get(`${API}/employees`, { withCredentials: true }),
          axios.get(`${API}/user-groups`, { withCredentials: true })
        ]);
        setEmployees(empRes.data);
        setGroups(grpRes.data);
      }
      
      // Check if user has an assignment for this talk
      const assignRes = await axios.get(`${API}/toolbox-talk-assignments`, { withCredentials: true });
      const myAssign = assignRes.data.find(a => a.talk_id === talkId);
      setMyAssignment(myAssign);
      
    } catch (error) {
      console.error("Error fetching talk:", error);
      toast.error("Failed to load toolbox talk");
      navigate('/toolbox-talks');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    const assignedTo = assignmentType === 'individual' ? selectedEmployees : selectedGroups;
    
    if (assignedTo.length === 0) {
      toast.error(`Please select at least one ${assignmentType === 'individual' ? 'employee' : 'group'}`);
      return;
    }

    setAssigning(true);
    try {
      await axios.post(`${API}/toolbox-talk-assignments`, {
        talk_id: talkId,
        assignment_type: assignmentType,
        assigned_to: assignedTo
      }, { withCredentials: true });
      
      toast.success("Toolbox talk assigned successfully!");
      setShowAssign(false);
      setSelectedEmployees([]);
      setSelectedGroups([]);
    } catch (error) {
      toast.error("Failed to assign toolbox talk");
    } finally {
      setAssigning(false);
    }
  };

  const handleComplete = async () => {
    if (!acknowledged) {
      toast.error("Please acknowledge that you have completed the toolbox talk");
      return;
    }
    
    if (!myAssignment) {
      toast.error("No assignment found");
      return;
    }

    setCompleting(true);
    try {
      await axios.post(
        `${API}/toolbox-talk-assignments/${myAssignment.assignment_id}/complete`,
        null,
        { 
          params: { notes: completionNotes || null },
          withCredentials: true 
        }
      );
      
      toast.success("Toolbox talk completed!");
      navigate('/toolbox-talks');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete toolbox talk");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!talk) return null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="toolbox-talk-view">
        {/* Header */}
        <div className="mb-8">
          <Link to="/toolbox-talks" className="inline-flex items-center text-muted-foreground hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Toolbox Talks
          </Link>
          
          <div className="flex items-start justify-between gap-4">
            <div>
              <Badge className="mb-2">{talk.category}</Badge>
              <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
                {talk.title}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {talk.duration_minutes} minutes
                </span>
                {talk.is_system && <Badge variant="outline">Pre-built</Badge>}
              </div>
            </div>
            
            {(user?.role === 'super_admin' || user?.role === 'company_admin') && (
              <Dialog open={showAssign} onOpenChange={setShowAssign}>
                <DialogTrigger asChild>
                  <Button data-testid="assign-talk-btn">
                    <Users className="w-4 h-4 mr-2" />
                    Assign
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Assign Toolbox Talk</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    {/* Assignment Type */}
                    <div className="flex gap-4">
                      <Button
                        variant={assignmentType === 'individual' ? 'default' : 'outline'}
                        onClick={() => setAssignmentType('individual')}
                        className="flex-1"
                      >
                        Individual
                      </Button>
                      <Button
                        variant={assignmentType === 'group' ? 'default' : 'outline'}
                        onClick={() => setAssignmentType('group')}
                        className="flex-1"
                      >
                        Group
                      </Button>
                    </div>

                    {/* Selection */}
                    {assignmentType === 'individual' ? (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        <Label>Select Employees</Label>
                        {employees.map((emp) => (
                          <div key={emp.employee_id} className="flex items-center gap-2 p-2 border rounded">
                            <Checkbox
                              checked={selectedEmployees.includes(emp.employee_id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedEmployees([...selectedEmployees, emp.employee_id]);
                                } else {
                                  setSelectedEmployees(selectedEmployees.filter(id => id !== emp.employee_id));
                                }
                              }}
                            />
                            <span>{emp.name}</span>
                          </div>
                        ))}
                        {employees.length === 0 && (
                          <p className="text-muted-foreground text-sm">No employees found</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        <Label>Select Groups</Label>
                        {groups.map((group) => (
                          <div key={group.group_id} className="flex items-center gap-2 p-2 border rounded">
                            <Checkbox
                              checked={selectedGroups.includes(group.group_id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedGroups([...selectedGroups, group.group_id]);
                                } else {
                                  setSelectedGroups(selectedGroups.filter(id => id !== group.group_id));
                                }
                              }}
                            />
                            <span>{group.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({group.member_ids?.length || 0} members)
                            </span>
                          </div>
                        ))}
                        {groups.length === 0 && (
                          <p className="text-muted-foreground text-sm">No groups found. Create groups first.</p>
                        )}
                      </div>
                    )}

                    <Button onClick={handleAssign} disabled={assigning} className="w-full">
                      {assigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Assign Talk
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Description */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <p className="text-slate-700">{talk.description}</p>
          </CardContent>
        </Card>

        {/* Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Talk Content</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-slate max-w-none">
              {talk.content?.split('\n').map((paragraph, i) => (
                <p key={i} className="mb-4 whitespace-pre-wrap">{paragraph}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Key Points */}
        {talk.key_points?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Key Points</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {talk.key_points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Discussion Questions */}
        {talk.discussion_questions?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Discussion Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {talk.discussion_questions.map((question, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>{question}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Completion Section (for employees with assignment) */}
        {myAssignment && !myAssignment.completions?.some(c => c.employee_id === user?.user_id) && (
          <Card className="border-accent">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-accent">Complete This Talk</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="acknowledge"
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  data-testid="acknowledge-checkbox"
                />
                <Label htmlFor="acknowledge" className="font-normal cursor-pointer">
                  I acknowledge that I have read and understood this toolbox talk and will apply the safety principles discussed.
                </Label>
              </div>
              
              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder="Any questions or comments..."
                  className="mt-1.5"
                  data-testid="completion-notes"
                />
              </div>

              <Button 
                onClick={handleComplete} 
                disabled={completing || !acknowledged}
                className="w-full"
                data-testid="complete-talk-btn"
              >
                {completing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Mark as Completed
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ToolboxTalkView;
